import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { DashboardLayout } from '@/components/DashboardLayout';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Separator } from '@/components/ui/separator';
import { api } from '@/api';
import { toast } from 'sonner';
import {
  ArrowLeft, Upload, Send, CheckCircle2, XCircle, Clock, AlertTriangle,
  FileText, Download, Loader2, Calendar, Building2, Truck, Package,
  MessageSquare, RefreshCw, Bell, ChevronRight, Edit, Eye, Plus,
  AlertCircle, CheckCircle, User, Mail, Link2, Sparkles, ListChecks,
  Target, FileWarning, RotateCcw, Copy, ExternalLink, Brain, Paperclip,
  ChevronDown, ChevronUp, X
} from 'lucide-react';
import ShopDrawingTimeline from '@/components/shop-drawings/ShopDrawingTimeline';

const STATUSES = [
  'Draft', 'In Preparation', 'Submitted to Contractor', 'Returned by Contractor',
  'Submitted to Consultant', 'Under Consultant Review', 'Approved',
  'Approved with Comments', 'Rejected', 'Superseded', 'Closed', 'On Hold'
];

const STATUS_COLORS = {
  'Draft': 'bg-slate-100 text-slate-700',
  'In Preparation': 'bg-blue-100 text-blue-700',
  'Submitted to Contractor': 'bg-purple-100 text-purple-700',
  'Returned by Contractor': 'bg-amber-100 text-amber-700',
  'Submitted to Consultant': 'bg-indigo-100 text-indigo-700',
  'Under Consultant Review': 'bg-cyan-100 text-cyan-700',
  'Approved': 'bg-green-100 text-green-700',
  'Approved with Comments': 'bg-emerald-100 text-emerald-700',
  'Rejected': 'bg-red-100 text-red-700',
  'Superseded': 'bg-gray-100 text-gray-700',
  'Closed': 'bg-slate-100 text-slate-700',
  'On Hold': 'bg-orange-100 text-orange-700'
};

const CYCLE_STATUS_COLORS = {
  'Open': 'bg-blue-100 text-blue-700',
  'Awaiting Contractor': 'bg-purple-100 text-purple-700',
  'Awaiting Consultant': 'bg-cyan-100 text-cyan-700',
  'Awaiting Resubmission': 'bg-amber-100 text-amber-700',
  'Completed': 'bg-green-100 text-green-700'
};

const DECISION_COLORS = {
  'Approved': 'bg-green-100 text-green-700',
  'Approved with Comments': 'bg-emerald-100 text-emerald-700',
  'Rejected': 'bg-red-100 text-red-700',
  'Pending': 'bg-slate-100 text-slate-700'
};

const RISK_COLORS = {
  'Green': 'bg-green-100 text-green-700 border-green-200',
  'Amber': 'bg-amber-100 text-amber-700 border-amber-200',
  'Red': 'bg-red-100 text-red-700 border-red-200'
};

const REJECTION_CATEGORIES = [
  'Design Non-Compliance',
  'Coordination Clash',
  'Missing Details',
  'Dimensions',
  'Specification',
  'Material',
  'Other'
];

const ShopDrawingDetail = () => {
  const { packageId } = useParams();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [pkg, setPkg] = useState(null);
  const [activeTab, setActiveTab] = useState('overview');

  // Dialogs
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const [contractorActionDialog, setContractorActionDialog] = useState(false);
  const [consultantResponseDialog, setConsultantResponseDialog] = useState(false);
  const [createCycleDialog, setCreateCycleDialog] = useState(false);
  const [editStatusDialogOpen, setEditStatusDialogOpen] = useState(false);
  const [submitToConsultantDialog, setSubmitToConsultantDialog] = useState(false);

  // Consultants list
  const [consultants, setConsultants] = useState([]);
  const [loadingConsultants, setLoadingConsultants] = useState(false);

  // Action states
  const [uploading, setUploading] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [fileRole, setFileRole] = useState('Shop Drawing');
  const [fileDescription, setFileDescription] = useState('');
  const [newStatus, setNewStatus] = useState('');
  
  // Subcontractor portal
  const [portalLinkDialogOpen, setPortalLinkDialogOpen] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const fileInputRef = useRef(null);
  const [portalLink, setPortalLink] = useState(null);
  const [generatingPortalLink, setGeneratingPortalLink] = useState(false);

  // Resubmission form
  const [resubmitReason, setResubmitReason] = useState('');
  const [resubmitNotes, setResubmitNotes] = useState('');
  const [resubmitRevisionNotes, setResubmitRevisionNotes] = useState('');
  const [resubmitFiles, setResubmitFiles] = useState([]);

  // Communication log
  const [expandedLogEntry, setExpandedLogEntry] = useState(null);
  const [aiAnalysis, setAiAnalysis] = useState(null);
  const [analyzingCorrespondence, setAnalyzingCorrespondence] = useState(false);

  // Contractor action
  const [contractorAction, setContractorAction] = useState('approve_for_consultant');
  const [contractorComments, setContractorComments] = useState('');

  // Consultant response
  const [consultantDecision, setConsultantDecision] = useState('');
  const [consultantComments, setConsultantComments] = useState('');
  const [rejectionCategory, setRejectionCategory] = useState('');

  // Submit to consultant form
  const [submitForm, setSubmitForm] = useState({
    consultant_ids: [],
    cc_ids: [],
    cover_letter: '',
    notes: ''
  });
  const [submitFiles, setSubmitFiles] = useState([]);
  const [submitting, setSubmitting] = useState(false);

  const loadPackage = useCallback(async () => {
    try {
      setLoading(true);
      const response = await api.get(`/shop-drawings/${packageId}`);
      setPkg(response.data);
      
      // Load consultants (professional team) for this project
      if (response.data?.project_id) {
        try {
          setLoadingConsultants(true);
          const consultantsRes = await api.get(`/projects/${response.data.project_id}/professional-team`);
          setConsultants(consultantsRes.data || []);
        } catch (err) {
          console.error('Failed to load consultants:', err);
        } finally {
          setLoadingConsultants(false);
        }
      }
    } catch (error) {
      toast.error('Failed to load package');
    } finally {
      setLoading(false);
    }
  }, [packageId]);

  useEffect(() => {
    loadPackage();
  }, [loadPackage]);

  const handleAnalyzeCorrespondence = async () => {
    setAnalyzingCorrespondence(true);
    setAiAnalysis(null);
    try {
      const res = await api.correspondence.analyzeModuleCorrespondence('sd', packageId);
      setAiAnalysis(res.data);
      toast.success('AI analysis complete');
    } catch (error) {
      toast.error('AI analysis failed');
    } finally {
      setAnalyzingCorrespondence(false);
    }
  };

  const handleDownloadEml = async (correspondenceId) => {
    try {
      const res = await api.correspondence.downloadCorrespondenceEml(correspondenceId);
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'email.eml');
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      toast.error('Failed to download email');
    }
  };

  const handleDownloadAttachment = async (correspondenceId, attachmentId, filename) => {
    try {
      const res = await api.correspondence.downloadCorrespondenceAttachment(correspondenceId, attachmentId);
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename || 'attachment');
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      toast.error('Failed to download attachment');
    }
  };

  const handleFileUpload = async () => {
    if (!selectedFile) {
      toast.error('Please select a file');
      return;
    }

    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', selectedFile);
      formData.append('file_role', fileRole);
      formData.append('description', fileDescription);
      formData.append('uploaded_by_party', 'Contractor');

      await api.post(`/shop-drawings/${packageId}/files`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      toast.success('File uploaded');
      setUploadDialogOpen(false);
      setSelectedFile(null);
      setFileDescription('');
      loadPackage();
    } catch (error) {
      toast.error('Failed to upload file');
    } finally {
      setUploading(false);
    }
  };

  const handleContractorAction = async () => {
    setProcessing(true);
    try {
      await api.post(`/shop-drawings/cycles/${pkg.current_cycle_id}/contractor-action`, {
        action: contractorAction,
        comments: contractorComments
      });

      toast.success(
        contractorAction === 'approve_for_consultant'
          ? 'Submitted to consultant'
          : 'Returned to subcontractor'
      );
      setContractorActionDialog(false);
      setContractorComments('');
      loadPackage();
    } catch (error) {
      toast.error('Action failed');
    } finally {
      setProcessing(false);
    }
  };

  const handleConsultantResponse = async () => {
    if (!consultantDecision) {
      toast.error('Please select a decision');
      return;
    }

    if ((consultantDecision === 'Rejected' || consultantDecision === 'Approved with Comments') && !consultantComments) {
      toast.error('Comments are required');
      return;
    }

    if (consultantDecision === 'Rejected' && !rejectionCategory) {
      toast.error('Rejection category is required');
      return;
    }

    setProcessing(true);
    try {
      await api.post(`/shop-drawings/cycles/${pkg.current_cycle_id}/consultant-response`, {
        decision: consultantDecision,
        comment_summary: consultantComments,
        rejection_category: rejectionCategory || null
      });

      toast.success('Response submitted');
      setConsultantResponseDialog(false);
      setConsultantDecision('');
      setConsultantComments('');
      setRejectionCategory('');
      loadPackage();
    } catch (error) {
      toast.error('Failed to submit response');
    } finally {
      setProcessing(false);
    }
  };

  const handleCreateCycle = async () => {
    if (!resubmitReason.trim()) {
      toast.error('Please provide a reason for resubmission');
      return;
    }
    setProcessing(true);
    try {
      const newCycle = await api.post(`/shop-drawings/${packageId}/cycles`, {
        notes: resubmitNotes,
        reason: resubmitReason,
        revision_notes: resubmitRevisionNotes,
      });

      // Upload any attached files to the new cycle
      if (resubmitFiles.length > 0 && newCycle.data?.id) {
        for (const file of resubmitFiles) {
          const formData = new FormData();
          formData.append('file', file);
          formData.append('file_role', 'Shop Drawing');
          formData.append('description', `Resubmission - Cycle ${newCycle.data.cycle_number}`);
          try {
            await api.post(`/shop-drawings/${packageId}/files?cycle_id=${newCycle.data.id}`, formData, {
              headers: { 'Content-Type': 'multipart/form-data' }
            });
          } catch (fileErr) {
            console.error('Failed to upload resubmission file:', fileErr);
          }
        }
      }

      toast.success(`Resubmission Cycle #${newCycle.data?.cycle_number || ''} created`);
      setCreateCycleDialog(false);
      setResubmitReason('');
      setResubmitNotes('');
      setResubmitRevisionNotes('');
      setResubmitFiles([]);
      loadPackage();
    } catch (error) {
      toast.error('Failed to create cycle');
    } finally {
      setProcessing(false);
    }
  };

  const handleSubmitToConsultant = async () => {
    if (submitForm.consultant_ids.length === 0) {
      toast.error('Please select at least one consultant');
      return;
    }
    
    if (!pkg.files || pkg.files.length === 0) {
      toast.error('Please upload at least one file before submitting');
      return;
    }
    
    setSubmitting(true);
    try {
      // Submit to consultant with email
      await api.post(`/shop-drawings/${packageId}/submit-to-consultant`, {
        consultant_ids: submitForm.consultant_ids,
        cc_ids: submitForm.cc_ids,
        cover_letter: submitForm.cover_letter,
        notes: submitForm.notes,
        send_email: true,
        attach_files: true
      });
      
      toast.success('Shop drawing submitted to consultant');
      setSubmitToConsultantDialog(false);
      setSubmitForm({
        consultant_ids: [],
        cc_ids: [],
        cover_letter: '',
        notes: ''
      });
      loadPackage();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit to consultant');
    } finally {
      setSubmitting(false);
    }
  };

  const handleSendReminder = async (reminderType) => {
    try {
      await api.post(`/shop-drawings/cycles/${pkg.current_cycle_id}/send-reminder`, null, {
        params: { reminder_type: reminderType }
      });
      toast.success(`Reminder sent to ${reminderType}`);
    } catch (error) {
      toast.error('Failed to send reminder');
    }
  };

  const handleCreateRFI = async () => {
    try {
      const response = await api.post(`/shop-drawings/${packageId}/create-rfi`);
      toast.success('RFI created successfully');
      // Navigate to the RFI detail page for editing
      navigate(`/projects/${pkg?.project_id}/rfi/${response.data.rfi.id}`);
    } catch (error) {
      toast.error('Failed to create RFI');
    }
  };

  const handleCreateDelayEvent = async () => {
    try {
      const response = await api.post(`/shop-drawings/${packageId}/create-delay-event`);
      toast.success('Delay event created');
    } catch (error) {
      toast.error('Failed to create delay event');
    }
  };

  const handleUpdateStatus = async () => {
    if (!newStatus) {
      toast.error('Please select a status');
      return;
    }
    setProcessing(true);
    try {
      await api.put(`/shop-drawings/${packageId}`, { status: newStatus });
      toast.success('Status updated');
      setEditStatusDialogOpen(false);
      setNewStatus('');
      loadPackage();
    } catch (error) {
      toast.error('Failed to update status');
    } finally {
      setProcessing(false);
    }
  };

  const handleGeneratePortalLink = async () => {
    setGeneratingPortalLink(true);
    try {
      const response = await api.post(`/shop-drawings/${packageId}/generate-subcontractor-link`, { expires_in_days: 30 });
      setPortalLink(response.data);
      setPortalLinkDialogOpen(true);
      toast.success('Portal link generated');
    } catch (error) {
      toast.error('Failed to generate portal link');
    } finally {
      setGeneratingPortalLink(false);
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Copied to clipboard');
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric'
    });
  };

  const formatDateTime = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-96">
          <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
        </div>
      </DashboardLayout>
    );
  }

  if (!pkg) {
    return (
      <DashboardLayout>
        <div className="p-6 text-center">
          <p className="text-slate-500">Package not found</p>
        </div>
      </DashboardLayout>
    );
  }

  const currentCycle = pkg.cycles?.find(c => c.id === pkg.current_cycle_id);
  const isOverdue = currentCycle?.is_contractor_overdue || currentCycle?.is_consultant_overdue || currentCycle?.is_resubmission_overdue;

  return (
    <DashboardLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="space-y-3">
          <div className="flex items-start gap-2 sm:gap-4">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate(-1)}
              className="rounded-sm mt-1 shrink-0"
              data-testid="back-btn"
            >
              <ArrowLeft className="h-4 w-4" />
            </Button>
            <div className="min-w-0 flex-1">
              <div className="flex flex-wrap items-center gap-2">
                <h1 className="text-lg sm:text-xl font-barlow font-bold uppercase tracking-wider text-slate-900">
                  {pkg.package_ref}
                </h1>
                <button
                  onClick={() => { setNewStatus(pkg.status); setEditStatusDialogOpen(true); }}
                  className="group"
                  data-testid="edit-status-btn"
                >
                  <Badge className={`rounded-sm cursor-pointer hover:ring-2 hover:ring-blue-400 text-xs ${STATUS_COLORS[pkg.status] || ''}`}>
                    {pkg.status}
                    <Edit className="h-3 w-3 ml-1 opacity-0 group-hover:opacity-100 transition-opacity" />
                  </Badge>
                </button>
                <Badge className={`rounded-sm text-xs ${RISK_COLORS[pkg.risk_level] || ''}`}>
                  {pkg.risk_level} Risk
                </Badge>
                {isOverdue && (
                  <Badge className="rounded-sm bg-red-100 text-red-700 text-xs">
                    <AlertTriangle className="h-3 w-3 mr-1" />
                    Overdue
                  </Badge>
                )}
              </div>
              <p className="text-base sm:text-lg text-slate-700 mt-1 truncate">{pkg.title}</p>
              <div className="flex flex-wrap items-center gap-x-3 gap-y-1 mt-2 text-sm text-slate-500">
                <span className="whitespace-nowrap">
                  <Building2 className="h-4 w-4 inline mr-1" />
                  {pkg.trade}
                </span>
                <span className="whitespace-nowrap">
                  <User className="h-4 w-4 inline mr-1" />
                  {pkg.subcontractor_name}
                </span>
                {pkg.work_area && (
                  <span className="whitespace-nowrap">
                    <Package className="h-4 w-4 inline mr-1" />
                    {pkg.work_area}
                  </span>
                )}
                <Badge variant="outline" className="rounded-sm font-semibold bg-slate-50 text-xs">
                  <RotateCcw className="h-3 w-3 mr-1" />
                  Revision {pkg.current_cycle_number || 0}
                </Badge>
              </div>
            </div>
          </div>

          {/* Action buttons - wrapped for mobile */}
          <div className="flex flex-wrap items-center gap-2 pl-0 sm:pl-12">
            {pkg.status === 'Draft' && (
              <>
                <Button
                  onClick={() => setUploadDialogOpen(true)}
                  className="rounded-sm text-xs sm:text-sm"
                  size="sm"
                  data-testid="upload-files-btn"
                >
                  <Upload className="h-4 w-4 sm:mr-1.5" />
                  <span className="hidden sm:inline">Upload Files</span>
                  <span className="sm:hidden ml-1">Upload</span>
                </Button>
                {pkg.files && pkg.files.length > 0 && (
                  <Button
                    onClick={() => setSubmitToConsultantDialog(true)}
                    className="rounded-sm bg-indigo-600 hover:bg-indigo-700 text-xs sm:text-sm"
                    size="sm"
                    data-testid="submit-to-consultant-btn"
                  >
                    <Send className="h-4 w-4 sm:mr-1.5" />
                    <span className="hidden sm:inline">Submit to Consultant</span>
                    <span className="sm:hidden ml-1">Submit</span>
                  </Button>
                )}
              </>
            )}

            {pkg.status === 'Submitted to Contractor' && (
              <>
                <Button
                  onClick={() => setContractorActionDialog(true)}
                  className="rounded-sm bg-purple-600 hover:bg-purple-700 text-xs sm:text-sm"
                  size="sm"
                  data-testid="review-submit-btn"
                >
                  <CheckCircle2 className="h-4 w-4 sm:mr-1.5" />
                  <span className="hidden sm:inline">Review & Submit</span>
                  <span className="sm:hidden ml-1">Review</span>
                </Button>
                <Button
                  onClick={() => setSubmitToConsultantDialog(true)}
                  className="rounded-sm bg-indigo-600 hover:bg-indigo-700 text-xs sm:text-sm"
                  size="sm"
                  data-testid="submit-to-consultant-btn"
                >
                  <Send className="h-4 w-4 sm:mr-1.5" />
                  <span className="hidden sm:inline">Submit to Consultant</span>
                  <span className="sm:hidden ml-1">Submit</span>
                </Button>
              </>
            )}

            {(pkg.status === 'Submitted to Consultant' || pkg.status === 'Under Consultant Review') && (
              <>
                <Button
                  variant="outline"
                  onClick={() => handleSendReminder('consultant')}
                  className="rounded-sm text-xs sm:text-sm"
                  size="sm"
                  data-testid="send-reminder-btn"
                >
                  <Bell className="h-4 w-4 sm:mr-1.5" />
                  <span className="hidden sm:inline">Reminder</span>
                </Button>
                <Button
                  onClick={() => setConsultantResponseDialog(true)}
                  className="rounded-sm bg-cyan-600 hover:bg-cyan-700 text-xs sm:text-sm"
                  size="sm"
                  data-testid="record-response-btn"
                >
                  <MessageSquare className="h-4 w-4 sm:mr-1.5" />
                  <span className="hidden sm:inline">Record Response</span>
                  <span className="sm:hidden ml-1">Response</span>
                </Button>
              </>
            )}

            {(pkg.status === 'Rejected' || pkg.status === 'Returned by Contractor') && (
              <>
                <Button
                  onClick={() => setSubmitToConsultantDialog(true)}
                  className="rounded-sm bg-indigo-600 hover:bg-indigo-700 text-xs sm:text-sm"
                  size="sm"
                  data-testid="submit-to-consultant-btn"
                >
                  <Send className="h-4 w-4 sm:mr-1.5" />
                  <span className="hidden sm:inline">Submit to Consultant</span>
                  <span className="sm:hidden ml-1">Submit</span>
                </Button>
              </>
            )}

            {pkg.total_cycles >= 1 && pkg.status !== 'Draft' && pkg.status !== 'VOIDED' && (
              <Button
                onClick={() => setCreateCycleDialog(true)}
                className="rounded-sm bg-amber-600 hover:bg-amber-700 text-xs sm:text-sm"
                size="sm"
                data-testid="start-resubmission-btn"
              >
                <RefreshCw className="h-4 w-4 sm:mr-1.5" />
                <span className="ml-1 sm:ml-0">Resubmit</span>
              </Button>
            )}

            {pkg.status !== 'Draft' && (
              <Button
                variant="outline"
                onClick={() => setUploadDialogOpen(true)}
                className="rounded-sm text-xs sm:text-sm"
                size="sm"
                data-testid="upload-btn"
              >
                <Upload className="h-4 w-4 sm:mr-1.5" />
                <span className="hidden sm:inline">Upload</span>
              </Button>
            )}

            <Button
              variant="outline"
              onClick={handleGeneratePortalLink}
              disabled={generatingPortalLink}
              className="rounded-sm text-xs sm:text-sm"
              size="sm"
              data-testid="generate-portal-link-btn"
            >
              {generatingPortalLink ? (
                <Loader2 className="h-4 w-4 sm:mr-1.5 animate-spin" />
              ) : (
                <ExternalLink className="h-4 w-4 sm:mr-1.5" />
              )}
              <span className="hidden sm:inline">Portal</span>
            </Button>
          </div>
        </div>

        {/* Key Dates & Metrics */}
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3 sm:gap-4">
          <Card className="border-slate-200">
            <CardContent className="p-3 sm:pt-4">
              <p className="text-xs font-bold uppercase tracking-wider text-slate-500">Lead Time</p>
              <p className="text-lg sm:text-xl font-bold text-slate-900 mt-1">
                {(pkg.manufacturing_lead_time_days || 0) + (pkg.delivery_lead_time_days || 0)} days
              </p>
              <p className="text-xs text-slate-500">Approval to delivery</p>
            </CardContent>
          </Card>

          <Card className={`border-slate-200 ${pkg.required_on_site_date ? '' : 'opacity-50'}`}>
            <CardContent className="p-3 sm:pt-4">
              <p className="text-xs font-bold uppercase tracking-wider text-slate-500">Required On Site</p>
              <p className="text-base sm:text-lg font-bold text-slate-900 mt-1">
                {formatDate(pkg.required_on_site_date)}
              </p>
            </CardContent>
          </Card>

          <Card className={`border-slate-200 ${pkg.estimated_delivery_date ? '' : 'opacity-50'}`}>
            <CardContent className="p-3 sm:pt-4">
              <p className="text-xs font-bold uppercase tracking-wider text-slate-500">Est. Delivery</p>
              <p className="text-base sm:text-lg font-bold text-slate-900 mt-1">
                {formatDate(pkg.estimated_delivery_date)}
              </p>
            </CardContent>
          </Card>

          <Card className="border-slate-200">
            <CardContent className="p-3 sm:pt-4">
              <p className="text-xs font-bold uppercase tracking-wider text-slate-500">Resubmissions</p>
              <p className="text-lg sm:text-xl font-bold text-slate-900 mt-1">
                {pkg.number_of_resubmissions}
              </p>
            </CardContent>
          </Card>

          <Card className={`col-span-2 sm:col-span-1 ${pkg.delivery_risk_days < 0 ? 'border-red-200 bg-red-50/50' : 'border-green-200 bg-green-50/50'}`}>
            <CardContent className="p-3 sm:pt-4">
              <p className="text-xs font-bold uppercase tracking-wider text-slate-500">Delivery Risk</p>
              <p className={`text-lg sm:text-xl font-bold mt-1 ${pkg.delivery_risk_days < 0 ? 'text-red-600' : 'text-green-600'}`}>
                {pkg.delivery_risk_days > 0 ? '+' : ''}{pkg.delivery_risk_days || 0} days
              </p>
            </CardContent>
          </Card>
        </div>

        {/* Tabs */}
        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <div className="overflow-x-auto -mx-1 px-1">
            <TabsList className="bg-slate-100 rounded-sm w-max sm:w-auto">
              <TabsTrigger value="overview" className="rounded-sm text-xs sm:text-sm">Overview</TabsTrigger>
              <TabsTrigger value="cycles" className="rounded-sm text-xs sm:text-sm">
                Cycles ({pkg.cycles?.length || 0})
              </TabsTrigger>
              <TabsTrigger value="files" className="rounded-sm text-xs sm:text-sm">
                Files ({pkg.files?.length || 0})
              </TabsTrigger>
              <TabsTrigger value="timeline" className="rounded-sm text-xs sm:text-sm">Timeline</TabsTrigger>
              <TabsTrigger value="communication" className="rounded-sm text-xs sm:text-sm" data-testid="sd-communication-tab">
                Comms ({pkg.communication_log?.length || 0})
              </TabsTrigger>
            </TabsList>
          </div>

          {/* Overview Tab */}
          <TabsContent value="overview" className="mt-4">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Package Details */}
              <Card className="border-slate-200">
                <CardHeader>
                  <CardTitle className="text-sm font-bold uppercase tracking-wider">
                    Package Details
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  {pkg.description && (
                    <div>
                      <p className="text-xs font-bold uppercase tracking-wider text-slate-500">Description</p>
                      <p className="text-sm text-slate-700 mt-1">{pkg.description}</p>
                    </div>
                  )}

                  {pkg.scope && (
                    <div>
                      <p className="text-xs font-bold uppercase tracking-wider text-slate-500">Scope</p>
                      <p className="text-sm text-slate-700 mt-1">{pkg.scope}</p>
                    </div>
                  )}

                  <Separator />

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <p className="text-xs font-bold uppercase tracking-wider text-slate-500">Subcontractor</p>
                      <p className="text-sm text-slate-700 mt-1">{pkg.subcontractor_name}</p>
                    </div>
                    <div>
                      <p className="text-xs font-bold uppercase tracking-wider text-slate-500">Trade</p>
                      <p className="text-sm text-slate-700 mt-1">{pkg.trade}</p>
                    </div>
                    <div>
                      <p className="text-xs font-bold uppercase tracking-wider text-slate-500">Priority</p>
                      <p className="text-sm text-slate-700 mt-1">{pkg.priority}</p>
                    </div>
                    <div>
                      <p className="text-xs font-bold uppercase tracking-wider text-slate-500">Work Area</p>
                      <p className="text-sm text-slate-700 mt-1">{pkg.work_area || '-'}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Current Cycle */}
              {currentCycle && (
                <Card className={`border-slate-200 ${isOverdue ? 'border-red-200' : ''}`}>
                  <CardHeader>
                    <CardTitle className="text-sm font-bold uppercase tracking-wider flex items-center justify-between">
                      <span>Current Cycle #{currentCycle.cycle_number}</span>
                      <Badge className={`rounded-sm ${CYCLE_STATUS_COLORS[currentCycle.cycle_status] || ''}`}>
                        {currentCycle.cycle_status}
                      </Badge>
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-4">
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <p className="text-xs font-bold uppercase tracking-wider text-slate-500">Uploaded</p>
                        <p className="text-sm text-slate-700 mt-1">
                          {formatDateTime(currentCycle.subbie_upload_date)}
                        </p>
                      </div>
                      <div>
                        <p className="text-xs font-bold uppercase tracking-wider text-slate-500">Submitted to Consultant</p>
                        <p className="text-sm text-slate-700 mt-1">
                          {formatDateTime(currentCycle.submitted_to_consultant_date)}
                        </p>
                      </div>
                    </div>

                    {currentCycle.due_dates && (
                      <div className="bg-slate-50 rounded-sm p-3">
                        <p className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-2">Due Dates</p>
                        <div className="space-y-1 text-sm">
                          {currentCycle.due_dates.contractor_review_due && (
                            <div className={`flex justify-between ${currentCycle.is_contractor_overdue ? 'text-red-600' : ''}`}>
                              <span>Contractor Review:</span>
                              <span className="font-medium">
                                {formatDate(currentCycle.due_dates.contractor_review_due)}
                                {currentCycle.is_contractor_overdue && <AlertTriangle className="h-3 w-3 inline ml-1" />}
                              </span>
                            </div>
                          )}
                          {currentCycle.due_dates.consultant_response_due && (
                            <div className={`flex justify-between ${currentCycle.is_consultant_overdue ? 'text-red-600' : ''}`}>
                              <span>Consultant Response:</span>
                              <span className="font-medium">
                                {formatDate(currentCycle.due_dates.consultant_response_due)}
                                {currentCycle.is_consultant_overdue && <AlertTriangle className="h-3 w-3 inline ml-1" />}
                              </span>
                            </div>
                          )}
                          {currentCycle.due_dates.resubmission_due && (
                            <div className={`flex justify-between ${currentCycle.is_resubmission_overdue ? 'text-red-600' : ''}`}>
                              <span>Resubmission:</span>
                              <span className="font-medium">
                                {formatDate(currentCycle.due_dates.resubmission_due)}
                                {currentCycle.is_resubmission_overdue && <AlertTriangle className="h-3 w-3 inline ml-1" />}
                              </span>
                            </div>
                          )}
                        </div>
                      </div>
                    )}

                    {currentCycle.consultant_decision && currentCycle.consultant_decision !== 'Pending' && (
                      <div className="border-t pt-4">
                        <div className="flex items-center gap-2 mb-2">
                          <p className="text-xs font-bold uppercase tracking-wider text-slate-500">Decision</p>
                          <Badge className={`rounded-sm ${DECISION_COLORS[currentCycle.consultant_decision] || ''}`}>
                            {currentCycle.consultant_decision}
                          </Badge>
                        </div>
                        {currentCycle.consultant_comment_summary && (
                          <p className="text-sm text-slate-700 bg-slate-50 p-3 rounded-sm">
                            {currentCycle.consultant_comment_summary}
                          </p>
                        )}
                        {currentCycle.rejection_reason_category && (
                          <p className="text-sm text-red-600 mt-2">
                            Reason: {currentCycle.rejection_reason_category}
                          </p>
                        )}
                      </div>
                    )}
                  </CardContent>
                </Card>
              )}
            </div>

            {/* AI Analysis - Amendment Summary */}
            {currentCycle?.ai_analysis && (
              <Card className="border-slate-200 mt-6 border-l-4 border-l-purple-400" data-testid="ai-analysis-card">
                <CardHeader>
                  <CardTitle className="text-sm font-bold uppercase tracking-wider flex items-center gap-2">
                    <Sparkles className="h-4 w-4 text-purple-500" />
                    AI Analysis - Consultant Feedback Summary
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  {/* Summary */}
                  {currentCycle.ai_analysis.summary && (
                    <div className="bg-purple-50 p-4 rounded-sm">
                      <p className="text-sm text-slate-700">{currentCycle.ai_analysis.summary}</p>
                    </div>
                  )}

                  {/* Key Issues Requested by Consultant */}
                  {currentCycle?.ai_analysis?.key_issues?.length > 0 && (
                    <div>
                      <p className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-2 flex items-center gap-2">
                        <FileWarning className="h-4 w-4 text-amber-500" />
                        Amendments Requested by Consultant
                      </p>
                      <div className="space-y-2">
                        {(currentCycle.ai_analysis.key_issues || []).map((issue, idx) => (
                          <div key={idx} className="flex items-start gap-3 bg-amber-50 p-3 rounded-sm border-l-2 border-l-amber-400">
                            <span className={`text-xs font-bold px-2 py-0.5 rounded ${
                              issue.severity === 'Critical' ? 'bg-red-100 text-red-700' :
                              issue.severity === 'Major' ? 'bg-amber-100 text-amber-700' :
                              'bg-slate-100 text-slate-700'
                            }`}>{issue.severity || 'Info'}</span>
                            <div className="flex-1">
                              <p className="text-sm font-medium text-slate-800">{issue.issue}</p>
                              <p className="text-xs text-slate-500 mt-1">
                                {issue.type && <span className="mr-2">Type: {issue.type}</span>}
                                {issue.affected_area && <span>Area: {issue.affected_area}</span>}
                              </p>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Action Items - What Subcontractor Needs To Do */}
                  {currentCycle?.ai_analysis?.action_items?.length > 0 && (
                    <div>
                      <p className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-2 flex items-center gap-2">
                        <ListChecks className="h-4 w-4 text-blue-500" />
                        Amendments Required from Subcontractor
                      </p>
                      <div className="space-y-2">
                        {(currentCycle.ai_analysis.action_items || []).map((item, idx) => (
                          <div key={idx} className="flex items-start gap-3 bg-blue-50 p-3 rounded-sm border-l-2 border-l-blue-400">
                            <span className={`text-xs font-bold px-2 py-0.5 rounded ${
                              item.priority === 'High' ? 'bg-red-100 text-red-700' :
                              item.priority === 'Medium' ? 'bg-amber-100 text-amber-700' :
                              'bg-green-100 text-green-700'
                            }`}>{item.priority}</span>
                            <div className="flex-1">
                              <p className="text-sm text-slate-800">{item.action}</p>
                              {item.responsible_party && (
                                <p className="text-xs text-slate-500 mt-1">Responsible: {item.responsible_party}</p>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* What Changed / Missing */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {currentCycle.ai_analysis.what_changed && (
                      <div className="bg-slate-50 p-3 rounded-sm">
                        <p className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-1">What Needs to Change</p>
                        <p className="text-sm text-slate-700">{currentCycle.ai_analysis.what_changed}</p>
                      </div>
                    )}
                    {currentCycle.ai_analysis.what_is_missing && (
                      <div className="bg-slate-50 p-3 rounded-sm">
                        <p className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-1">What is Missing</p>
                        <p className="text-sm text-slate-700">{currentCycle.ai_analysis.what_is_missing}</p>
                      </div>
                    )}
                  </div>

                  {/* Recommendations */}
                  {currentCycle?.ai_analysis?.recommendations?.length > 0 && (
                    <div>
                      <p className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-2 flex items-center gap-2">
                        <Target className="h-4 w-4 text-green-500" />
                        Recommendations
                      </p>
                      <ul className="list-disc list-inside space-y-1 text-sm text-slate-700 pl-2">
                        {(currentCycle.ai_analysis.recommendations || []).map((rec, idx) => (
                          <li key={idx}>{rec}</li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {/* Risk Assessment */}
                  {currentCycle.ai_analysis.delay_risk && (
                    <div className={`p-3 rounded-sm ${
                      currentCycle.ai_analysis.delay_risk.risk_level === 'High' ? 'bg-red-50 border border-red-200' :
                      currentCycle.ai_analysis.delay_risk.risk_level === 'Medium' ? 'bg-amber-50 border border-amber-200' :
                      'bg-green-50 border border-green-200'
                    }`}>
                      <p className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-1">Delay Risk Assessment</p>
                      <p className="text-sm">
                        <span className={`font-semibold ${
                          currentCycle.ai_analysis.delay_risk.risk_level === 'High' ? 'text-red-700' :
                          currentCycle.ai_analysis.delay_risk.risk_level === 'Medium' ? 'text-amber-700' :
                          'text-green-700'
                        }`}>{currentCycle.ai_analysis.delay_risk.risk_level} Risk</span>
                        {currentCycle.ai_analysis.delay_risk.reason && (
                          <span className="text-slate-600"> - {currentCycle.ai_analysis.delay_risk.reason}</span>
                        )}
                      </p>
                    </div>
                  )}

                  <p className="text-xs text-slate-400 mt-2">
                    Analyzed at {formatDateTime(currentCycle.ai_analysis.analyzed_at)} using {currentCycle.ai_analysis.model_used || 'AI'}
                  </p>
                </CardContent>
              </Card>
            )}

          </TabsContent>

          {/* Cycles Tab */}
          <TabsContent value="cycles" className="mt-4">
            <Card className="border-slate-200">
              <CardContent className="p-0">
                <div className="space-y-4 p-4">
                  {pkg.cycles?.map((cycle, index) => (
                    <div
                      key={cycle.id}
                      className={`border rounded-sm p-4 ${cycle.id === pkg.current_cycle_id ? 'border-blue-300 bg-blue-50/30' : 'border-slate-200'}`}
                    >
                      <div className="flex items-start justify-between">
                        <div>
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="font-bold">Cycle #{cycle.cycle_number}</span>
                            <Badge className={`rounded-sm ${CYCLE_STATUS_COLORS[cycle.cycle_status] || ''}`}>
                              {cycle.cycle_status}
                            </Badge>
                            {cycle.consultant_decision && cycle.consultant_decision !== 'Pending' && (
                              <Badge className={`rounded-sm ${DECISION_COLORS[cycle.consultant_decision] || ''}`}>
                                {cycle.consultant_decision}
                              </Badge>
                            )}
                            {cycle.id === pkg.current_cycle_id && (
                              <Badge className="rounded-sm bg-blue-600 text-white text-xs">Current</Badge>
                            )}
                          </div>
                          {cycle.resubmission_reason && (
                            <p className="text-xs text-amber-700 bg-amber-50 px-2 py-1 rounded mt-2 inline-block">
                              <RefreshCw className="h-3 w-3 inline mr-1" />
                              {cycle.resubmission_reason}
                            </p>
                          )}
                          <div className="text-sm text-slate-500 mt-2 space-y-1">
                            <p>Uploaded: {formatDateTime(cycle.subbie_upload_date)}</p>
                            {cycle.submitted_to_consultant_date && (
                              <p>Submitted: {formatDateTime(cycle.submitted_to_consultant_date)}</p>
                            )}
                            {cycle.consultant_response_date && (
                              <p>Response: {formatDateTime(cycle.consultant_response_date)}</p>
                            )}
                          </div>
                        </div>
                        <div className="text-right text-sm">
                          {cycle.contractor_review_duration_days !== null && (
                            <p>Contractor Review: {cycle.contractor_review_duration_days} days</p>
                          )}
                          {cycle.consultant_review_duration_days !== null && (
                            <p>Consultant Review: {cycle.consultant_review_duration_days} days</p>
                          )}
                          {cycle.total_cycle_duration_days !== null && (
                            <p className="font-medium">Total: {cycle.total_cycle_duration_days} days</p>
                          )}
                        </div>
                      </div>
                      {cycle.revision_notes && (
                        <div className="mt-3 bg-blue-50 p-3 rounded-sm border-l-2 border-l-blue-400">
                          <p className="text-xs font-bold uppercase text-slate-500">Revision Notes</p>
                          <p className="text-sm text-slate-700 mt-1">{cycle.revision_notes}</p>
                        </div>
                      )}
                      {cycle.subcontractor_notes && (
                        <div className="mt-2 bg-slate-50 p-3 rounded-sm">
                          <p className="text-xs font-bold uppercase text-slate-500">Notes</p>
                          <p className="text-sm text-slate-700 mt-1">{cycle.subcontractor_notes}</p>
                        </div>
                      )}
                      {cycle.consultant_comment_summary && (
                        <div className="mt-2 bg-slate-100 p-3 rounded-sm">
                          <p className="text-xs font-bold uppercase text-slate-500">Consultant Comments</p>
                          <p className="text-sm text-slate-700 mt-1">{cycle.consultant_comment_summary}</p>
                        </div>
                      )}
                    </div>
                  ))}

                  {(!pkg.cycles || pkg.cycles.length === 0) && (
                    <div className="text-center py-8">
                      <p className="text-slate-500">No submission cycles yet</p>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Files Tab */}
          <TabsContent value="files" className="mt-4">
            <Card className="border-slate-200">
              <CardHeader className="flex flex-row items-center justify-between">
                <CardTitle className="text-sm font-bold uppercase tracking-wider">Files</CardTitle>
                <div className="flex gap-2">
                  <Button size="sm" variant="outline" onClick={loadPackage} className="rounded-sm" data-testid="refresh-files-btn">
                    <RefreshCw className="h-4 w-4" />
                  </Button>
                  <Button size="sm" onClick={() => setUploadDialogOpen(true)} className="rounded-sm">
                    <Upload className="h-4 w-4 mr-2" />
                    Upload
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                <div className="space-y-2">
                  {pkg.files?.map((file) => (
                    <div
                      key={file.id}
                      className="flex items-center justify-between p-3 border border-slate-200 rounded-sm hover:bg-slate-50"
                    >
                      <div className="flex items-center gap-3">
                        <FileText className="h-5 w-5 text-slate-400" />
                        <div>
                          <p className="font-medium text-sm">{file.original_filename}</p>
                          <p className="text-xs text-slate-500">
                            {file.file_role} • {file.uploaded_by_party} • {formatDateTime(file.uploaded_at)}
                          </p>
                        </div>
                      </div>
                      <Button 
                        variant="ghost" 
                        size="sm"
                        onClick={() => {
                          // Build download URL
                          const baseUrl = process.env.REACT_APP_BACKEND_URL || '';
                          const fileUrl = file.file_url?.startsWith('/') 
                            ? `${baseUrl}${file.file_url}` 
                            : file.file_url;
                          if (fileUrl) {
                            window.open(fileUrl, '_blank');
                          } else {
                            toast.error('File URL not available');
                          }
                        }}
                        data-testid={`download-file-${file.id}`}
                      >
                        <Download className="h-4 w-4" />
                      </Button>
                    </div>
                  ))}

                  {(!pkg.files || pkg.files.length === 0) && (
                    <div className="text-center py-8">
                      <p className="text-slate-500">No files uploaded</p>
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* Timeline Tab */}
          <TabsContent value="timeline" className="mt-4">
            <ShopDrawingTimeline packageId={packageId} projectId={pkg?.project_id} />
          </TabsContent>

          {/* Communication Log Tab */}
          <TabsContent value="communication" className="mt-4">
            <div className="bg-white border border-slate-200 rounded-sm p-4 sm:p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-sm font-bold uppercase tracking-wider text-slate-500">
                  Communication Log ({pkg.communication_log?.length || 0})
                </h3>
                {pkg.communication_log?.length > 0 && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleAnalyzeCorrespondence}
                    disabled={analyzingCorrespondence}
                    data-testid="sd-ai-analyze-btn"
                    className="text-xs"
                  >
                    {analyzingCorrespondence ? (
                      <><Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> Analyzing...</>
                    ) : (
                      <><Brain className="h-3.5 w-3.5 mr-1.5" /> AI Analyze</>
                    )}
                  </Button>
                )}
              </div>

              {aiAnalysis && (
                <div className="mb-4 bg-indigo-50 border border-indigo-200 rounded-sm p-4" data-testid="sd-ai-analysis-result">
                  <div className="flex items-center justify-between mb-3">
                    <div className="flex items-center gap-2">
                      <Brain className="h-4 w-4 text-indigo-600" />
                      <span className="text-sm font-semibold text-indigo-700">AI Correspondence Analysis</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-xs text-indigo-500">{aiAnalysis.summary?.total_messages} messages analyzed</span>
                      <Button variant="ghost" size="sm" onClick={() => setAiAnalysis(null)} className="h-6 w-6 p-0">
                        <X className="h-3.5 w-3.5" />
                      </Button>
                    </div>
                  </div>
                  <div className="prose prose-sm max-w-none text-slate-700 whitespace-pre-wrap text-sm leading-relaxed">
                    {aiAnalysis.analysis}
                  </div>
                </div>
              )}

              {pkg.communication_log?.length > 0 ? (
                <div className="space-y-4">
                  {(pkg.communication_log || []).map((entry) => (
                    <div
                      key={entry.id}
                      className={`rounded-sm border overflow-hidden ${
                        entry.is_consultant_response
                          ? 'bg-green-50 border-green-200'
                          : entry.type === 'email_reply'
                          ? 'bg-blue-50 border-blue-200'
                          : 'bg-slate-50 border-slate-200'
                      }`}
                    >
                      <div
                        className="p-4 cursor-pointer hover:bg-opacity-70 transition-colors"
                        onClick={() => setExpandedLogEntry(expandedLogEntry === entry.id ? null : entry.id)}
                        data-testid={`sd-comm-log-entry-${entry.id}`}
                      >
                        <div className="flex items-start justify-between mb-2">
                          <div className="flex items-center gap-2">
                            {entry.is_consultant_response ? (
                              <Badge className="bg-green-100 text-green-700">Response</Badge>
                            ) : entry.type === 'email_reply' ? (
                              <Badge className="bg-blue-100 text-blue-700">
                                <Mail className="h-3 w-3 mr-1" />Email Reply
                              </Badge>
                            ) : (
                              <Badge className="bg-slate-100 text-slate-700">{entry.type}</Badge>
                            )}
                            <span className="text-sm font-medium">{entry.from_name}</span>
                            {entry.from_email && entry.type === 'email_reply' && (
                              <span className="text-xs text-slate-400">&lt;{entry.from_email}&gt;</span>
                            )}
                          </div>
                          <div className="flex items-center gap-2">
                            <span className="text-xs text-slate-500">{formatDateTime(entry.date || entry.created_at)}</span>
                            {expandedLogEntry === entry.id ? (
                              <ChevronUp className="h-4 w-4 text-slate-400" />
                            ) : (
                              <ChevronDown className="h-4 w-4 text-slate-400" />
                            )}
                          </div>
                        </div>
                        {entry.subject && (
                          <p className="text-sm font-medium text-slate-700 mb-1">{entry.subject}</p>
                        )}
                        {expandedLogEntry !== entry.id && (entry.body || entry.message) && (
                          <p className="text-sm text-slate-600 line-clamp-2">{entry.body || entry.message}</p>
                        )}
                        {entry.attachments?.length > 0 && expandedLogEntry !== entry.id && (
                          <div className="flex items-center gap-2 mt-2 text-blue-600">
                            <Paperclip className="h-3 w-3" />
                            <span className="text-xs">{(entry.attachments || []).length} attachment(s)</span>
                          </div>
                        )}
                      </div>

                      {expandedLogEntry === entry.id && (
                        <div className="border-t border-slate-200 bg-white">
                          {(entry.body || entry.message) && (
                            <div className="p-4 border-b border-slate-100">
                              <p className="text-xs font-semibold text-slate-500 uppercase mb-2">Message</p>
                              <p className="text-sm text-slate-700 whitespace-pre-wrap">{entry.body || entry.message}</p>
                            </div>
                          )}
                          {(entry.from_email || entry.to_emails?.length > 0) && (
                            <div className="p-4 border-b border-slate-100 bg-slate-50">
                              <p className="text-xs font-semibold text-slate-500 uppercase mb-2">Email Info</p>
                              <div className="space-y-1 text-sm">
                                {entry.from_email && (
                                  <p><span className="text-slate-500">From:</span> {entry.from_name} &lt;{entry.from_email}&gt;</p>
                                )}
                                {entry.to_emails?.length > 0 && (
                                  <p><span className="text-slate-500">To:</span> {entry.to_emails.join(', ')}</p>
                                )}
                                {entry.cc_emails?.length > 0 && (
                                  <p><span className="text-slate-500">CC:</span> {entry.cc_emails.join(', ')}</p>
                                )}
                              </div>
                            </div>
                          )}
                          {entry.correspondence_id && (
                            <div className="p-4 border-b border-slate-100 flex gap-2">
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={(e) => { e.stopPropagation(); handleDownloadEml(entry.correspondence_id); }}
                                className="text-xs"
                              >
                                <Download className="h-3.5 w-3.5 mr-1.5" />
                                Download Original Email (.eml)
                              </Button>
                            </div>
                          )}
                          {entry.attachments?.length > 0 && (
                            <div className="p-4">
                              <p className="text-xs font-semibold text-slate-500 uppercase mb-3">
                                Attachments ({(entry.attachments || []).length})
                              </p>
                              <div className="space-y-2">
                                {(entry.attachments || []).map((att, attIdx) => (
                                  <div
                                    key={attIdx}
                                    className="flex items-center justify-between p-3 bg-slate-50 rounded border border-slate-200 hover:border-blue-300 transition-colors"
                                  >
                                    <div className="flex items-center gap-3">
                                      <Paperclip className="h-5 w-5 text-slate-500" />
                                      <div>
                                        <p className="text-sm font-medium text-slate-700">{att.filename || att.file_name || 'Attachment'}</p>
                                        {(att.size || att.file_size) && (
                                          <p className="text-xs text-slate-400">{((att.size || att.file_size) / 1024).toFixed(1)} KB</p>
                                        )}
                                      </div>
                                    </div>
                                    {entry.correspondence_id && att.id ? (
                                      <Button
                                        variant="outline"
                                        size="sm"
                                        onClick={(e) => { e.stopPropagation(); handleDownloadAttachment(entry.correspondence_id, att.id, att.filename || att.file_name); }}
                                        className="text-xs"
                                      >
                                        <Download className="h-3 w-3 mr-1" /> Download
                                      </Button>
                                    ) : (att.file_url || att.url) ? (
                                      <a href={att.file_url || att.url} target="_blank" rel="noopener noreferrer"
                                        className="flex items-center gap-1 px-3 py-1.5 bg-blue-600 text-white text-xs font-medium rounded hover:bg-blue-700"
                                        onClick={(e) => e.stopPropagation()}>
                                        <Download className="h-3 w-3" /> Download
                                      </a>
                                    ) : null}
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}
                          {(!entry.attachments || entry.attachments.length === 0) && !entry.body && !entry.message && (
                            <div className="p-4 text-center text-slate-400 text-sm">No additional details available</div>
                          )}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-slate-500">No communication logged yet. Replies to system emails will appear here.</p>
              )}
            </div>
          </TabsContent>
        </Tabs>

        {/* Upload Dialog */}
        <Dialog open={uploadDialogOpen} onOpenChange={(open) => {
          setUploadDialogOpen(open);
          if (!open) { setSelectedFile(null); setIsDragging(false); setFileDescription(''); if (fileInputRef.current) fileInputRef.current.value = ''; }
        }}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle>Upload File</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div>
                <Label>File</Label>
                <input
                  type="file"
                  ref={fileInputRef}
                  className="hidden"
                  onChange={(e) => {
                    if (e.target.files?.[0]) setSelectedFile(e.target.files[0]);
                  }}
                  data-testid="file-input-hidden"
                />
                <div
                  data-testid="file-dropzone"
                  onClick={() => fileInputRef.current?.click()}
                  onDragOver={(e) => { e.preventDefault(); e.stopPropagation(); setIsDragging(true); }}
                  onDragEnter={(e) => { e.preventDefault(); e.stopPropagation(); setIsDragging(true); }}
                  onDragLeave={(e) => { e.preventDefault(); e.stopPropagation(); setIsDragging(false); }}
                  onDrop={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    setIsDragging(false);
                    const droppedFile = e.dataTransfer.files?.[0];
                    if (droppedFile) setSelectedFile(droppedFile);
                  }}
                  className={`mt-1 flex flex-col items-center justify-center gap-2 rounded-lg border-2 border-dashed p-6 cursor-pointer transition-colors ${
                    isDragging
                      ? 'border-blue-500 bg-blue-50'
                      : selectedFile
                        ? 'border-green-400 bg-green-50'
                        : 'border-slate-300 bg-slate-50 hover:border-slate-400 hover:bg-slate-100'
                  }`}
                >
                  {selectedFile ? (
                    <>
                      <FileText className="h-8 w-8 text-green-600" />
                      <p className="text-sm font-medium text-green-700 text-center break-all">{selectedFile.name}</p>
                      <p className="text-xs text-slate-500">{(selectedFile.size / 1024).toFixed(1)} KB</p>
                      <button
                        type="button"
                        onClick={(e) => { e.stopPropagation(); setSelectedFile(null); if (fileInputRef.current) fileInputRef.current.value = ''; }}
                        className="text-xs text-red-500 hover:text-red-700 underline"
                        data-testid="remove-file-btn"
                      >
                        Remove
                      </button>
                    </>
                  ) : (
                    <>
                      <Upload className={`h-8 w-8 ${isDragging ? 'text-blue-500' : 'text-slate-400'}`} />
                      <p className="text-sm font-medium text-slate-600">
                        {isDragging ? 'Drop file here' : 'Drag & drop a file here'}
                      </p>
                      <p className="text-xs text-slate-400">or click to browse</p>
                    </>
                  )}
                </div>
              </div>
              <div>
                <Label>File Role</Label>
                <Select value={fileRole} onValueChange={setFileRole}>
                  <SelectTrigger className="mt-1">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Shop Drawing">Shop Drawing</SelectItem>
                    <SelectItem value="Marked Up">Marked Up</SelectItem>
                    <SelectItem value="Supporting Doc">Supporting Doc</SelectItem>
                    <SelectItem value="Specification">Specification</SelectItem>
                    <SelectItem value="Calculation">Calculation</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Description</Label>
                <Textarea
                  value={fileDescription}
                  onChange={(e) => setFileDescription(e.target.value)}
                  className="mt-1"
                  rows={2}
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => { setSelectedFile(null); setFileDescription(''); setIsDragging(false); if (fileInputRef.current) fileInputRef.current.value = ''; setUploadDialogOpen(false); }}>Cancel</Button>
              <Button onClick={handleFileUpload} disabled={uploading}>
                {uploading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Upload className="h-4 w-4 mr-2" />}
                Upload
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Contractor Action Dialog */}
        <Dialog open={contractorActionDialog} onOpenChange={setContractorActionDialog}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle>Contractor Review</DialogTitle>
              <DialogDescription>Review and take action on this submission</DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div>
                <Label>Action</Label>
                <Select value={contractorAction} onValueChange={setContractorAction}>
                  <SelectTrigger className="mt-1">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="approve_for_consultant">Submit to Consultant</SelectItem>
                    <SelectItem value="return_to_subbie">Return to Subcontractor</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label>Comments</Label>
                <Textarea
                  value={contractorComments}
                  onChange={(e) => setContractorComments(e.target.value)}
                  className="mt-1"
                  rows={3}
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setContractorActionDialog(false)}>Cancel</Button>
              <Button onClick={handleContractorAction} disabled={processing}>
                {processing ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                {contractorAction === 'approve_for_consultant' ? 'Submit' : 'Return'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Consultant Response Dialog */}
        <Dialog open={consultantResponseDialog} onOpenChange={setConsultantResponseDialog}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle>Record Consultant Response</DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div>
                <Label>Decision *</Label>
                <Select value={consultantDecision} onValueChange={setConsultantDecision}>
                  <SelectTrigger className="mt-1">
                    <SelectValue placeholder="Select decision" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Approved">Approved</SelectItem>
                    <SelectItem value="Approved with Comments">Approved with Comments</SelectItem>
                    <SelectItem value="Rejected">Rejected</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              {consultantDecision === 'Rejected' && (
                <div>
                  <Label>Rejection Category *</Label>
                  <Select value={rejectionCategory} onValueChange={setRejectionCategory}>
                    <SelectTrigger className="mt-1">
                      <SelectValue placeholder="Select category" />
                    </SelectTrigger>
                    <SelectContent>
                      {REJECTION_CATEGORIES.map(cat => (
                        <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
              <div>
                <Label>Comments {(consultantDecision === 'Rejected' || consultantDecision === 'Approved with Comments') && '*'}</Label>
                <Textarea
                  value={consultantComments}
                  onChange={(e) => setConsultantComments(e.target.value)}
                  className="mt-1"
                  rows={4}
                  placeholder="Enter consultant comments..."
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setConsultantResponseDialog(false)}>Cancel</Button>
              <Button onClick={handleConsultantResponse} disabled={processing}>
                {processing ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                Submit Response
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Create Cycle Dialog */}
        <Dialog open={createCycleDialog} onOpenChange={setCreateCycleDialog}>
          <DialogContent className="sm:max-w-lg">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <RefreshCw className="h-5 w-5 text-amber-600" />
                Start Resubmission — Cycle #{(pkg.total_cycles || 0) + 1}
              </DialogTitle>
              <DialogDescription>
                Create a new submission cycle for {pkg.package_ref}. Previous cycle history will be preserved.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-2">
              <div>
                <Label className="text-xs font-bold uppercase tracking-wider">Reason for Resubmission *</Label>
                <Select value={resubmitReason} onValueChange={setResubmitReason}>
                  <SelectTrigger className="mt-1 rounded-sm" data-testid="resubmit-reason-select">
                    <SelectValue placeholder="Select reason..." />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Rejected by Consultant">Rejected by Consultant</SelectItem>
                    <SelectItem value="Approved with Comments - Revisions Required">Approved with Comments - Revisions Required</SelectItem>
                    <SelectItem value="Updated Drawings">Updated Drawings</SelectItem>
                    <SelectItem value="Design Change">Design Change</SelectItem>
                    <SelectItem value="Coordination Clash Resolution">Coordination Clash Resolution</SelectItem>
                    <SelectItem value="Missing Information Added">Missing Information Added</SelectItem>
                    <SelectItem value="Client Requested Changes">Client Requested Changes</SelectItem>
                    <SelectItem value="Other">Other</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs font-bold uppercase tracking-wider">Revision Notes</Label>
                <Textarea
                  value={resubmitRevisionNotes}
                  onChange={(e) => setResubmitRevisionNotes(e.target.value)}
                  placeholder="Describe what has been revised or changed in this resubmission..."
                  rows={3}
                  className="mt-1 rounded-sm"
                  data-testid="resubmit-revision-notes"
                />
              </div>
              <div>
                <Label className="text-xs font-bold uppercase tracking-wider">Additional Notes</Label>
                <Textarea
                  value={resubmitNotes}
                  onChange={(e) => setResubmitNotes(e.target.value)}
                  placeholder="Any additional notes for the contractor/consultant..."
                  rows={2}
                  className="mt-1 rounded-sm"
                  data-testid="resubmit-notes"
                />
              </div>
              <div>
                <Label className="text-xs font-bold uppercase tracking-wider">Attach Updated Files (optional)</Label>
                <div className="mt-1 border-2 border-dashed border-slate-200 rounded-sm p-4 text-center">
                  <input
                    type="file"
                    multiple
                    onChange={(e) => setResubmitFiles(Array.from(e.target.files))}
                    className="hidden"
                    id="resubmit-file-input"
                    data-testid="resubmit-file-input"
                  />
                  <label htmlFor="resubmit-file-input" className="cursor-pointer">
                    <Upload className="h-6 w-6 text-slate-400 mx-auto mb-1" />
                    <p className="text-sm text-slate-500">Click to select files</p>
                    <p className="text-xs text-slate-400">PDF, DWG, IFC, etc.</p>
                  </label>
                  {resubmitFiles.length > 0 && (
                    <div className="mt-3 space-y-1 text-left">
                      {resubmitFiles.map((f, i) => (
                        <div key={i} className="flex items-center gap-2 text-sm">
                          <Paperclip className="h-3.5 w-3.5 text-slate-400" />
                          <span className="text-slate-700 truncate">{f.name}</span>
                          <span className="text-xs text-slate-400 shrink-0">({(f.size / 1024).toFixed(0)}KB)</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setCreateCycleDialog(false)} className="rounded-sm">Cancel</Button>
              <Button
                onClick={handleCreateCycle}
                disabled={processing || !resubmitReason}
                className="rounded-sm bg-amber-600 hover:bg-amber-700"
                data-testid="confirm-resubmit-btn"
              >
                {processing ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <RefreshCw className="h-4 w-4 mr-2" />}
                Start Cycle #{(pkg.total_cycles || 0) + 1}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Edit Status Dialog */}
        <Dialog open={editStatusDialogOpen} onOpenChange={setEditStatusDialogOpen}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle>Update Package Status</DialogTitle>
              <DialogDescription>
                Change the status of shop drawing package {pkg.package_ref}
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <div>
                <Label className="text-xs font-bold uppercase tracking-wider">Status</Label>
                <Select value={newStatus} onValueChange={setNewStatus}>
                  <SelectTrigger className="mt-1 rounded-sm" data-testid="status-select">
                    <SelectValue placeholder="Select status" />
                  </SelectTrigger>
                  <SelectContent>
                    {STATUSES.map(s => (
                      <SelectItem key={s} value={s}>{s}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setEditStatusDialogOpen(false)} className="rounded-sm">
                Cancel
              </Button>
              <Button onClick={handleUpdateStatus} disabled={processing} className="rounded-sm" data-testid="update-status-btn">
                {processing ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <CheckCircle2 className="h-4 w-4 mr-2" />}
                Update Status
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Subcontractor Portal Link Dialog */}
        <Dialog open={portalLinkDialogOpen} onOpenChange={setPortalLinkDialogOpen}>
          <DialogContent className="sm:max-w-lg">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <ExternalLink className="h-5 w-5 text-indigo-600" />
                Subcontractor Portal Link
              </DialogTitle>
              <DialogDescription>
                Share this link with the subcontractor to allow them to view and upload files for this package.
              </DialogDescription>
            </DialogHeader>
            {portalLink && (
              <div className="space-y-4 py-4">
                <div>
                  <Label className="text-xs font-bold uppercase tracking-wider text-slate-500">Package</Label>
                  <p className="text-sm font-medium mt-1">{portalLink.package_ref} - {pkg.title}</p>
                </div>
                <div>
                  <Label className="text-xs font-bold uppercase tracking-wider text-slate-500">Subcontractor</Label>
                  <p className="text-sm font-medium mt-1">{portalLink.subcontractor_name}</p>
                </div>
                <div>
                  <Label className="text-xs font-bold uppercase tracking-wider text-slate-500">Portal Link</Label>
                  <div className="flex items-center gap-2 mt-1">
                    <input
                      type="text"
                      value={portalLink.portal_url}
                      readOnly
                      className="flex-1 text-sm p-2 bg-slate-50 border rounded-sm font-mono"
                    />
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => copyToClipboard(portalLink.portal_url)}
                      className="rounded-sm"
                    >
                      <Copy className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
                <div>
                  <Label className="text-xs font-bold uppercase tracking-wider text-slate-500">Expires</Label>
                  <p className="text-sm text-slate-600 mt-1">{formatDateTime(portalLink.expires_at)}</p>
                </div>
                <div className="bg-blue-50 p-3 rounded-sm border border-blue-200">
                  <p className="text-xs text-blue-700">
                    This link allows the subcontractor to view the package status, upload files, and download existing files.
                    The link will expire in 30 days.
                  </p>
                </div>
              </div>
            )}
            <DialogFooter>
              <Button
                variant="outline"
                onClick={() => window.open(portalLink?.portal_url, '_blank')}
                className="rounded-sm"
              >
                <ExternalLink className="h-4 w-4 mr-2" />
                Open Portal
              </Button>
              <Button onClick={() => setPortalLinkDialogOpen(false)} className="rounded-sm">
                Done
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Submit to Consultant Dialog */}
        <Dialog open={submitToConsultantDialog} onOpenChange={setSubmitToConsultantDialog}>
          <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Send className="h-5 w-5 text-indigo-600" />
                Submit to Consultant
              </DialogTitle>
              <DialogDescription>
                Submit shop drawing package {pkg?.package_ref} to consultants for review. Files will be attached to the email.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4 py-4">
              {/* Consultant Selection */}
              <div>
                <Label className="text-xs font-bold uppercase tracking-wider">Select Consultants *</Label>
                <p className="text-xs text-slate-500 mb-2">Select one or more consultants to receive this submission</p>
                {loadingConsultants ? (
                  <div className="flex items-center justify-center py-4">
                    <Loader2 className="h-5 w-5 animate-spin text-slate-400" />
                  </div>
                ) : (
                  <div className="space-y-2 max-h-40 overflow-y-auto border border-slate-200 rounded-sm p-2">
                    {consultants.length === 0 ? (
                      <p className="text-sm text-slate-500 text-center py-2">No consultants found for this project</p>
                    ) : (
                      consultants.map(consultant => (
                        <label 
                          key={consultant.id} 
                          className="flex items-center gap-2 p-2 hover:bg-slate-50 rounded cursor-pointer"
                        >
                          <input
                            type="checkbox"
                            checked={submitForm.consultant_ids.includes(consultant.id)}
                            onChange={(e) => {
                              if (e.target.checked) {
                                setSubmitForm(prev => ({
                                  ...prev,
                                  consultant_ids: [...prev.consultant_ids, consultant.id]
                                }));
                              } else {
                                setSubmitForm(prev => ({
                                  ...prev,
                                  consultant_ids: prev.consultant_ids.filter(id => id !== consultant.id)
                                }));
                              }
                            }}
                            className="rounded"
                          />
                          <div>
                            <p className="text-sm font-medium">{consultant.company_name}</p>
                            <p className="text-xs text-slate-500">{consultant.discipline} • {consultant.contact_email}</p>
                          </div>
                        </label>
                      ))
                    )}
                  </div>
                )}
              </div>

              {/* CC Recipients */}
              <div>
                <Label className="text-xs font-bold uppercase tracking-wider">CC (Optional)</Label>
                <p className="text-xs text-slate-500 mb-2">Additional recipients to copy on the submission</p>
                <div className="space-y-2 max-h-32 overflow-y-auto border border-slate-200 rounded-sm p-2">
                  {consultants.filter(c => !submitForm.consultant_ids.includes(c.id)).map(consultant => (
                    <label 
                      key={consultant.id} 
                      className="flex items-center gap-2 p-2 hover:bg-slate-50 rounded cursor-pointer"
                    >
                      <input
                        type="checkbox"
                        checked={submitForm.cc_ids.includes(consultant.id)}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setSubmitForm(prev => ({
                              ...prev,
                              cc_ids: [...prev.cc_ids, consultant.id]
                            }));
                          } else {
                            setSubmitForm(prev => ({
                              ...prev,
                              cc_ids: prev.cc_ids.filter(id => id !== consultant.id)
                            }));
                          }
                        }}
                        className="rounded"
                      />
                      <div>
                        <p className="text-sm">{consultant.company_name}</p>
                        <p className="text-xs text-slate-500">{consultant.discipline}</p>
                      </div>
                    </label>
                  ))}
                </div>
              </div>

              {/* Cover Letter */}
              <div>
                <Label className="text-xs font-bold uppercase tracking-wider">Cover Letter / Notes</Label>
                <Textarea
                  value={submitForm.cover_letter}
                  onChange={(e) => setSubmitForm(prev => ({ ...prev, cover_letter: e.target.value }))}
                  placeholder="Please review the attached shop drawings for approval..."
                  rows={4}
                  className="mt-1"
                />
              </div>

              {/* Files to be attached */}
              <div>
                <Label className="text-xs font-bold uppercase tracking-wider">Files to be Attached</Label>
                <div className="mt-1 border border-slate-200 rounded-sm p-3 bg-slate-50">
                  {pkg?.files && pkg.files.length > 0 ? (
                    <div className="space-y-2">
                      {pkg.files.map(file => (
                        <div key={file.id} className="flex items-center gap-2 text-sm">
                          <FileText className="h-4 w-4 text-slate-400" />
                          <span>{file.original_filename}</span>
                          <Badge variant="outline" className="text-xs">{file.file_role}</Badge>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p className="text-sm text-slate-500">No files uploaded yet</p>
                  )}
                </div>
              </div>

              {/* Info Banner */}
              <div className="bg-indigo-50 p-3 rounded-sm border border-indigo-200">
                <p className="text-xs text-indigo-700">
                  <strong>Note:</strong> An email with all attached files will be sent to the selected consultants. 
                  They will need to log in to the portal to submit their response.
                </p>
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setSubmitToConsultantDialog(false)} className="rounded-sm">
                Cancel
              </Button>
              <Button 
                onClick={handleSubmitToConsultant} 
                disabled={submitting || submitForm.consultant_ids.length === 0}
                className="rounded-sm bg-indigo-600 hover:bg-indigo-700"
              >
                {submitting ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Submitting...
                  </>
                ) : (
                  <>
                    <Send className="h-4 w-4 mr-2" />
                    Submit to Consultant
                  </>
                )}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
};

export default ShopDrawingDetail;
